import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import useOffboardingsApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import { useAuth } from '../../context/AuthContext';

const Container = styled.div``;

export default function OffboardingsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const history = useHistory();
  const writable = canWrite ? canWrite('OD') : false;

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Çalışanlar listesinden "İşten Ayrılış Süreci Başlat" ile gelindiğinde çekmeceye
  // önden konan değerler (çalışan + türetilen yönetici).
  const [prefill, setPrefill] = useState(null);
  const prefillHandled = useRef(false);

  const {
    fetching, data, pagination, columns: baseColumns, objectSchema,
    create, cancel, requestHandoverApproval, approveHandover, confirmItZeroAccess, close,
  } = useOffboardingsApi(queryParams);

  const handleNewButtonClick = useCallback(() => {
    setPrefill(null);
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setPrefill(null);
  }, []);

  const handleSave = useCallback(async (formData) => {
    const success = await create(formData);
    if (success) handleDrawerClose();
    return success;
  }, [create, handleDrawerClose]);

  // Çalışanlar listesindeki yönlendirmenin karşılığı. OD aktif kiracıda "İşten Çıkar"
  // aksiyonu YOK; çıkış buradan başlar ve kullanıcı çalışanı BİR KEZ DAHA seçmek
  // zorunda kalmamalı — aksi halde "yönlendirme" sadece bir sayfa değişimi olurdu.
  //
  // Yönetici alanı elle seçimdekiyle AYNI yoldan doldurulur: şemanın kendi
  // `deriveFields`'ı çağrılır (mantık kopyalanmaz). GenericFormDrawer türetmeyi yalnız
  // DEĞİŞİMDE çalıştırır, açılıştaki initialValues için değil — bu yüzden burada açıkça
  // tetikliyoruz.
  useEffect(() => {
    if (prefillHandled.current) return;
    const startForEmployeeId = history.location.state?.startForEmployeeId;
    if (!startForEmployeeId || !objectSchema || objectSchema.length === 0) return;

    prefillHandled.current = true;
    // Router state'i TÜKET: sayfa yenilendiğinde ya da geri gelindiğinde çekmece
    // kendiliğinden yeniden açılmasın.
    history.replace(history.location.pathname, null);

    const employeeField = objectSchema.find((f) => f.name === 'employeeId');
    const base = { employeeId: startForEmployeeId };

    Promise.resolve(employeeField?.deriveFields ? employeeField.deriveFields(startForEmployeeId) : {})
      .then((patch) => setPrefill({ ...base, ...(patch || {}) }))
      .catch(() => setPrefill(base))
      .finally(() => setDrawerOpen(true));
  }, [objectSchema, history]);

  const handleDataStateChange = useCallback((act, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const handleRowClick = useCallback((row) => {
    if (row?.id) history.push(`/offboarding/${row.id}`);
  }, [history]);

  const buildActions = useCallback((row) => {
    if (!row || !writable) return [];
    const actions = [{
      type: 'edit',
      label: t('offboarding.detail.viewProcess'),
      onClick: () => history.push(`/offboarding/${row.id}`),
    }];
    if (row.status === 'initiated' || row.status === 'in_progress') {
      actions.push({
        type: 'edit',
        label: t('offboarding.action.requestHandover'),
        onClick: () => setConfirm({
          message: t('offboarding.confirm.requestHandover', { person: row.personFullName }),
          onYes: () => requestHandoverApproval(row.id),
        }),
      });
    }
    if (row.status === 'pending_handover_approval') {
      actions.push({
        type: 'edit',
        label: t('offboarding.action.approveHandover'),
        onClick: () => setConfirm({
          message: t('offboarding.confirm.approveHandover', { person: row.personFullName }),
          onYes: () => approveHandover(row.id),
        }),
      });
    }
    if (row.status === 'pending_it_zero_access') {
      actions.push({
        type: 'edit',
        label: t('offboarding.action.confirmItZeroAccess'),
        onClick: () => setConfirm({
          message: t('offboarding.confirm.confirmItZeroAccess', { person: row.personFullName }),
          onYes: () => confirmItZeroAccess(row.id),
        }),
      });
    }
    if (row.status === 'pending_closure') {
      actions.push({
        type: 'edit',
        label: t('offboarding.action.closeWithAlumni'),
        onClick: () => setConfirm({
          message: t('offboarding.confirm.closeWithAlumni', { person: row.personFullName }),
          onYes: () => close(row.id, true),
        }),
      });
      actions.push({
        type: 'edit',
        label: t('offboarding.action.closeWithoutAlumni'),
        onClick: () => setConfirm({
          message: t('offboarding.confirm.closeWithoutAlumni', { person: row.personFullName }),
          onYes: () => close(row.id, false),
        }),
      });
    }
    if (row.status !== 'completed' && row.status !== 'cancelled') {
      actions.push({
        type: 'delete',
        label: t('offboarding.action.cancel'),
        onClick: () => setConfirm({
          message: t('offboarding.confirm.cancel', { person: row.personFullName }),
          onYes: () => cancel(row.id, ''),
        }),
      });
    }
    return actions;
  }, [t, history, writable, cancel, requestHandoverApproval, approveHandover, confirmItZeroAccess, close]);

  const columns = useMemo(() => {
    if (!writable) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const actions = buildActions(row);
            return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions, writable]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        initialValues={prefill}
        title={t('offboarding.new')}
        schema={objectSchema}
        onSave={handleSave}
      />

      {useMemo(() => (
        <DataTable
          title={t('offboarding.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={writable}
          onNewButtonClick={handleNewButtonClick}
          onRowClick={handleRowClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, writable, handleNewButtonClick,
        handleRowClick, filterList, handleFilterConfirm, handleDataStateChange,
      ])}
      <ConfirmDialog
        isOpen={!!confirm}
        message={confirm?.message}
        onConfirm={async () => { await confirm.onYes(); setConfirm(null); }}
        onCancel={() => setConfirm(null)}
      />
    </Container>
  );
}
